import React from 'react';

import { isDefined } from '@nfctron/nexus-core';

import { type AtomComponent } from '@root/lib/types/components';

import { useCartStore, useCartStoreExtraProductsProvider } from '../../stores/cartStore';

import { ConfigSheetProductAtom } from './ConfigSheetProduct.atom';
import { ConfigSheetVariantsAtom } from './ConfigSheetVariants.atom';

/**
 * Event/Cart/ConfigSheetAtom atom
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ConfigSheetAtom: AtomComponent = () => {
	/** get extra products provider from cart store */
	const extraProductsProvider = useCartStoreExtraProductsProvider();

	/** get sheet inputs from cart store */
	const productInput = useCartStore((s) => s.extraProductConfigSheetProductInput);
	const variantsInput = useCartStore((s) => s.extraProductConfigSheetVariantsInput);

	/** manage sheets open/close state */
	const [variantsSheetOpen, setVariantsSheetOpen] = React.useState(false);
	const [simpleSheetOpen, setSimpleSheetOpen] = React.useState(false);
	const [closing, setClosing] = React.useState(false);

	/** open closed sheet upon input config setting */
	React.useEffect(() => {
		if (!isDefined(productInput)) return;
		if (!simpleSheetOpen && !closing) setSimpleSheetOpen(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productInput]);

	/** open closed sheet upon input config setting */
	React.useEffect(() => {
		if (!isDefined(variantsInput)) return;
		if (!variantsSheetOpen && !closing) setVariantsSheetOpen(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [variantsInput]);

	return (
		<React.Fragment>
			{/* simple products sheet */}
			{isDefined(productInput) && (
				<ConfigSheetProductAtom
					open={simpleSheetOpen}
					dismiss={() => {
						setSimpleSheetOpen(false);
						setClosing(true);
						setTimeout(() => {
							extraProductsProvider.sheetConfig.setProductInput(null);
							setClosing(false);
						}, 500);
					}}
					input={productInput}
				/>
			)}
			{/* product group sheet */}
			{isDefined(variantsInput) && (
				<ConfigSheetVariantsAtom
					open={variantsSheetOpen}
					dismiss={() => {
						setVariantsSheetOpen(false);
						setClosing(true);
						setTimeout(() => {
							extraProductsProvider.sheetConfig.setVariantsInput(null);
							setClosing(false);
						}, 500);
					}}
					input={variantsInput}
				/>
			)}
		</React.Fragment>
	);
};

/**
 * Event/Cart/ConfigSheetAtom atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
ConfigSheetAtom.displayName = 'Event/Cart/ConfigSheetAtom';
